// Import components
import CreateFarmCalcAccordion from './CreateFarmCalcAccordion';

// Import utils
import { getChild, getChildren, sanitiseStringForID, setCorrespondingFieldValues, identifyFields } from './Utils/Utils';
import { calculationsController } from './Utils/Calculations';

/**
 * Create the dataset cloner.
 * 
 * @param {object}      options
 * 
 * @returns {object}
 */
const CreateClonableDataset = options => {
    const Dataset = options.dataset;
    const Add = getChild(Dataset, options.selectors.add);
    const MaxClones = Dataset.dataset.max ? parseInt(Dataset.dataset.max) : 999;
    let clones = [];
    let fieldData = [];
    let mounted = false;
    let remove = getChild(Dataset, options.selectors.remove);
    let sanitisedID = '';

    /**
     * Create the accordion for the nested dataset if required.
     * 
     * @param   {HTMLElement}   clone
     * 
     * @returns {void}
     */
    const __createAccordion = clone => {
        const FarmCalcAccordion = CreateFarmCalcAccordion({
            farmCalcAccordion: clone,
            animationTime: 200,
            isClone: true,
            attributes: {
                mount: 'data-mount'
            },
            classes: {
                active: 'active'
            },
            selectors: {
                button: '.farm-calc-accordion__button',
                container: '.farm-calc-accordion__container',
                dataset: '.farm-calc-accordion-dataset',
                dropdown: '.farm-calc-accordion__dropdown',
                number: '.farm-calc-accordion__button-number',
                title: '.farm-calc-accordion__button-title',
                error: '.farm-calc-accordion__error-msg'
            },
        });

        FarmCalcAccordion.init();
    };

    /**
     * Update the clone DOM tracking variable for the dataset.
     * 
     * @returns {void}
     */
    const __updateClonesTracker = () => {
        clones = getChildren(Dataset, options.selectors.clonable);
    };

    /**
     * Set a unique field ID and name for the element.
     * 
     * @param   {HTMLElement}   el
     * 
     * @returns {string}
     */
    const __setIdAriaAttributes = el => {
        const Button = getChild(el, options.selectors.button);
        const Dropdown = getChild(el, options.selectors.dropdown);
        sanitisedID = sanitiseStringForID(getChild(el, options.selectors.title).innerText);

        if (Button && Dropdown) {
            Button.setAttribute(options.attributes.ariaControls, `${sanitisedID}-${el.dataset.number}`);
            Dropdown.setAttribute(options.attributes.id, `${sanitisedID}-${el.dataset.number}`);
        }

        return el.dataset.number;
    };

    /**
     * Set unique field and label names for each input.
     * 
     * @param   {HTMLElement}   el
     * @param   {string}        number
     * 
     * @returns {void}
     */
    const __setFieldAndLabelNames = (el, number) => {
        const Tags = ['SELECT', 'INPUT', 'LABEL', 'TEXTAREA'];
        const Attributes = Object.values(options.attributes);

        // Loop through the tag selectors, identify any relevant elements
        // that require updating.
        Tags.forEach(tag => {
            const Els = getChildren(el, tag);

            // If we have elements, go through each and check if it has
            // the attributes specified in the Attributes array.
            if (Els) {
                Els.forEach(input => {
                    Attributes.forEach(attribute => {
                        // If we find the related attribute, sanitise it by stripping
                        // the hyphen and any numbers. Finally, apply the new attribute value.
                        if (input.getAttribute(attribute)) {
                            const SanitizedValue = input.getAttribute(attribute).replace(/\d+/g, '').replace('-', '');
                            input.setAttribute(
                                attribute,
                                `${SanitizedValue}-${number}`
                            );
                        }
                    });
                });
            }
        });
    };

    /**
     * Update the numbering counter on each of the nested accordions.
     * 
     * @returns {void}
     */
    const __updateCounter = () => {
        clones.forEach((clone, i) => {
            const Counter = getChild(clone, options.selectors.number);
            let number = null;

            if (Counter) {
                Counter.innerText = i + 1;
                clone.setAttribute(options.attributes.number, i + 1);
                number = __setIdAriaAttributes(clone);

                __setFieldAndLabelNames(clone, number);
            }
        });
    };

    /**
     * Send the data we need to create the corresponding field values
     * for each new clone we create.
     * 
     * @returns {void}
     */
    const __sendDataPacket = () => {
        const cloneNumber = clones.length;

        fieldData.forEach(item => {
            setCorrespondingFieldValues(item.packet, {
                isClone: true,
                primaryField: `${item.primaryField}-${cloneNumber}`,
                secondaryField: item.secondaryField ? `${item.secondaryField}-${cloneNumber}` : '',
                tertiaryField: item.tertiaryField ? `${item.tertiaryField}-${cloneNumber}` : '',
                updateSecondaryOptions: item.updateSecondaryOptions,
                updateTertiaryOptions: item.updateTertiaryOptions
            });
        });
    };

    /**
     * Empty the cloned input fields (not select fields).
     * 
     * @param   {HTMLElement}   clone
     * 
     * @returns {void}
     */
    const __emptyInputFields = clone => {
        const InputFields = getChildren(clone, 'INPUT');

        if (InputFields) {
            InputFields.forEach(field => {
                field.value = '';
            });
        }
    };

    /**
     * Add a new clone of the dataset.
     * 
     * @param   {object}    e
     * 
     * @returns {void}
     */
    const __addClone = e => {
        e.preventDefault();

        // Always clone the first item in the clone list.
        const Clone = clones[0].cloneNode(true);
        let ID = '';

        // Insert the clone as the last item in the list.
        clones[clones.length - 1].insertAdjacentElement('afterend', Clone);

        // Grab the relevant fields required for the calculationsController function
        fieldData = identifyFields(Clone);

        // Grab the sanitised data-id string from the parent, not the sibling accordion.
        ID = Clone.closest('.farm-calc-accordion:not(.farm-calc-accordion--nested)').dataset.id;

        __updateClonesTracker();
        __createAccordion(Clone);
        __updateCounter();
        __emptyInputFields(Clone);
        __bind(getChild(Clone, options.selectors.remove));

        if (fieldData) {
            __sendDataPacket();
            calculationsController(fieldData, ID);
        }

        // Flag that the clone has been mounted with all necessary listeners
        // and attributes. It will not be mounted again.
        Clone.setAttribute(options.attributes.mount, true);
    };

    /**
     * Remove cloned dataset.
     * 
     * @param {object}      e
     * 
     * @returns {void}
     */
    const __removeClone = e => {
        e.preventDefault();
        const sectionEl = e.target.closest('.clonable');
        const sectionId = sectionEl?.dataset.id;

        if (clones.length > 1) {
            // Look up the DOM inheritance tree and remove the
            // ancestor with clonable as a class.
            e.target.closest('.clonable').remove();
            
            // dispatch event so calcs can update their totals on remove
            document.dispatchEvent(new CustomEvent('clonable:removed', {
                detail: {
                    id: sectionId
                }
            }));

            __updateClonesTracker();
            __updateCounter();
        }
    }

    /**
     * Determine whether to disable the add button or not.
     * 
     * @param   {boolean}   show
     * 
     * @returns {void}
     */
    const __toggleDisableAdd = show => {
        Add.disabled = show;
    };

    /**
     * Bind the clone/delete functions to the remove button. This is
     * run every time a new clone is created.
     * 
     * @param   {HTMLElement}   remove
     * 
     * @returns {void}
     */
    const __bind = remove => {
        remove.addEventListener('click', e => {
            __removeClone(e);
            __toggleDisableAdd(false);
        });
    };

    /**
     * Init the component. Ensure that we have at least
     * one add button and one remove button present. We also
     * need to ensure that the dataset is not mounted prior
     * to running any of the remaining functions..
     * 
     * @returns {void}
     */
    const init = () => {
        mounted = Boolean(Dataset.getAttribute(options.attributes.mount));

        if (Add && remove && mounted !== true) {
            // Flagging a dataset as mounted so that whenever the constructor is
            // rerun, we do not remount everything.
            Dataset.setAttribute(options.attributes.mount, true);

            // Per dataset, there is only one add button. Cloning requires
            // an event listener to be added to each new remove button. We should
            // mount the add button once, this will prevent issues with multiple
            // instances of clones being thrown into the DOM.
            Add.addEventListener('click', e => {
                if (clones.length < MaxClones) {
                    __addClone(e);
                    __toggleDisableAdd(false);
                } else {
                    __toggleDisableAdd(true);
                }
            });

            __updateClonesTracker();
            __updateCounter();
            __bind(remove);
        }
    };

    /**
     * Return the publicly exposed init function.
     */
    return Object.freeze({ init });
};

export default CreateClonableDataset;
